import { OverlayModule } from '@angular/cdk/overlay';
import { ViewportRuler } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ElementRef,
  Input,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
  inject,
  DOCUMENT,
  output,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import {
  BehaviorSubject,
  NEVER,
  Observable,
  Subject,
  asyncScheduler,
  combineLatest,
  fromEvent,
  iif,
  merge,
  of,
} from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  observeOn,
  shareReplay,
  startWith,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { ColorsModule } from '@plugins/client/colors/colors.module';
import { ContrastColorPipe } from '@plugins/client/colors/contrast-color.pipe';
import { FormBaseControlComponent } from '@plugins/client/forms/form';
import { TextInputComponent } from '@plugins/client/forms/text-input/text-input.component';
import { ChevronIconComponent } from '@plugins/client/icons/chevron-icon';

import { AutocompleteNoOptionsDirective } from './autocomplete-no-options.directive';
import { AutocompleteOptionDirective } from './autocomplete-option.directive';

const OPTION_HOVER_COLOR = '#f2f2f2';

type CdkSetting = { flexibleDimensions: boolean; viewportMargin: number };

@Component({
  selector: 'plugins-autocomplete',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TextInputComponent,
    ColorsModule,
    OverlayModule,
    ChevronIconComponent,
  ],
  templateUrl: './autocomplete.component.html',
  styleUrl: './autocomplete.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutocompleteComponent<T extends object | string>
  extends FormBaseControlComponent<string>
  implements AfterViewInit
{
  BASE_MARGIN = 100;
  OPTIONS_MAX_HEIGHT = 250;
  @ViewChild(TextInputComponent) input?: TextInputComponent;
  @ViewChild('autocompleteRef') autocompleteRef?: ElementRef<HTMLUListElement>;
  @ViewChildren('optionRef') optionRefs!: QueryList<ElementRef<HTMLLIElement>>;

  @ContentChild(AutocompleteOptionDirective, { read: TemplateRef })
  optionTemplateRef?: TemplateRef<unknown>;
  @ContentChild(AutocompleteNoOptionsDirective, { read: TemplateRef })
  noOptionsTemplateRef?: TemplateRef<unknown>;

  @Input() options: T[] = [];
  @Input() placeholder = '';
  @Input() withSelect = false;
  @Input() trackByOption: (index: number, option: T) => string | number =
    this.defaultTrackBy.bind(this);
  @Input() autocompleteId = `auto-${Math.random().toString(16).slice(2)}`;

  /** Amount of margin that the options overlay should have with
   * respect to the viewport borders. Defaults to `100` */
  @Input() set viewportMargin(margin: number | undefined) {
    this.#viewportMargin.next(margin ?? 0);
  }

  get viewportMargin(): number {
    return this.#viewportMargin.getValue();
  }

  readonly selected = output<T>();
  readonly enterPressed = output();

  private viewportRuler = inject(ViewportRuler);
  private contrastPipe = inject(ContrastColorPipe);
  private document = inject(DOCUMENT);

  private _hoveredOptionIndex$ = new BehaviorSubject<number | null>(null);
  private _focusedOptionIndex$ = new BehaviorSubject<number | null>(null);
  focusedOptionIndex$ = this._focusedOptionIndex$.asObservable().pipe(
    tap((index) => {
      this._focusedOptionId$.next(
        index === null ? null : `opt-${index.toString()}`,
      );
    }),
  );
  private _focusedOptionId$ = new BehaviorSubject<string | null>(null);
  focusedOptionId$ = this._focusedOptionId$
    .asObservable()
    .pipe(distinctUntilChanged());
  optionStyles$?: Observable<Record<string, string>[]>;
  private closeOptions$ = new Subject<void>();
  private showOptions$ = new Subject<void>();
  optionsDisplayed$!: Observable<boolean>;
  autocompleteWidth$!: Observable<number>;
  #viewportMargin = new BehaviorSubject<number>(this.BASE_MARGIN);

  get generateCdkSettings(): CdkSetting {
    return {
      viewportMargin: this.viewportMargin,
      flexibleDimensions: !this.viewportMargin,
    };
  }
  #cdkEval = new Subject<void>();

  /** If body scrollHeight is less than the sum viewportMargin and maxHeight
   * flexible dimensions should be allowed and viewport margin should be 0. */
  cdk$: Observable<CdkSetting> = this.#cdkEval.pipe(
    switchMap(() =>
      iif(
        () =>
          this.document.body.scrollHeight <
          this.viewportMargin + this.OPTIONS_MAX_HEIGHT,
        of({ flexibleDimensions: true, viewportMargin: 0 }),
        of(this.generateCdkSettings),
      ),
    ),
    startWith(this.generateCdkSettings),
  );

  get textInputRef() {
    return this.input?.inputRef as ElementRef<HTMLInputElement>;
  }

  ngAfterViewInit() {
    this.setOptionsDisplayed();
    this.setAutocompleteWidth();
    this.setOptionStyles();
    this.setOptionsScrollIntoView();
  }

  onSelectOption(option: T) {
    if (typeof option === 'string') {
      this.control.setValue(option);
    }
    this.selected.emit(option);
    this.closeOptions$.next();
    this.input?.focus();
  }

  onKeydown(event: KeyboardEvent) {
    let focusIndex = this._focusedOptionIndex$.value;
    switch (event.key) {
      case 'Down':
      case 'ArrowDown':
        this.stopEvent(event);
        focusIndex =
          focusIndex !== null
            ? Math.min(focusIndex + 1, Math.max(this.options.length - 1, 0))
            : 0;
        this._focusedOptionIndex$.next(focusIndex);
        this.showOptions$.next();
        break;
      case 'Up':
      case 'ArrowUp':
        this.stopEvent(event);
        focusIndex =
          focusIndex !== null
            ? Math.max(focusIndex - 1, 0)
            : Math.max(this.options.length - 1, 0);
        this._focusedOptionIndex$.next(focusIndex);
        this.showOptions$.next();
        break;
      case 'Enter':
        this.stopEvent(event);
        if (focusIndex !== null) {
          this.onSelectOption(this.options[focusIndex]);
        } else {
          this.enterPressed.emit();
        }
        break;
      case 'Esc':
      case 'Escape':
        this.stopEvent(event);
        this.closeOptions$.next();
    }
  }

  onClick() {
    if (!this.withSelect) {
      return;
    }
    this.showOptions$.next();
  }

  onOptionHoverChange(index: number | null) {
    this._hoveredOptionIndex$.next(index);
  }

  override focus() {
    return this.input?.focus();
  }

  private setOptionsDisplayed() {
    const inputValueChange$ = this.control.valueChanges.pipe(
      distinctUntilChanged(),
      tap(() => {
        this._focusedOptionIndex$.next(null);
      }),
      shareReplay(1),
    );
    const focus$ = this.input?.focused$.pipe(
      // We don't want onBlur emit on initial load when the input is out of focus.
      filter((focused, i) => !(!i && !focused)),
      tap((focused) => {
        if (focused) {
          this.handleFocus();
        } else {
          this.handleBlur();
        }
      }),
    );
    const hide$ = merge(
      focus$?.pipe(
        filter((focused) => focused),
        switchMap(() =>
          fromEvent<FocusEvent>(
            this.textInputRef.nativeElement,
            'focusout',
          ).pipe(
            filter(
              (event) =>
                this.autocompleteRef?.nativeElement !== event.relatedTarget &&
                !this.autocompleteRef?.nativeElement.contains(
                  event.relatedTarget as Node,
                ),
            ),
          ),
        ),
      ) ?? NEVER,
      inputValueChange$.pipe(
        filter((val) => !val?.length && !this.noOptionsTemplateRef),
      ),
      this.closeOptions$,
    ).pipe(
      map(() => false),
      tap(() => {
        this._focusedOptionIndex$.next(null);
        this._hoveredOptionIndex$.next(null);
      }),
    );
    const show$ = merge(
      inputValueChange$.pipe(
        filter((val) => !!val?.length || !!this.noOptionsTemplateRef),
      ),
      this.showOptions$,
    ).pipe(
      tap(() => {
        this.#cdkEval.next();
      }),
      map(() => true),
    );
    this.optionsDisplayed$ = merge(hide$, show$).pipe(
      startWith(false),
      distinctUntilChanged(),
      shareReplay(1),
    );
  }

  private setAutocompleteWidth() {
    this.autocompleteWidth$ = merge(
      this.viewportRuler.change().pipe(
        debounceTime(300),
        withLatestFrom(this.optionsDisplayed$),
        filter(([_, displayed]) => displayed),
      ),
      this.optionsDisplayed$.pipe(filter((displayed) => displayed)),
    ).pipe(
      map(() => this.textInputRef.nativeElement.getBoundingClientRect().width),
    );
  }

  private setOptionStyles() {
    this.optionStyles$ = this.optionRefs.changes.pipe(
      observeOn(asyncScheduler),
      switchMap(() =>
        combineLatest([
          this.focusedOptionIndex$,
          this._hoveredOptionIndex$,
        ]).pipe(
          map(([focusedIndex, hoveredIndex]) =>
            this.options.map((_option, index) => {
              const hovered = hoveredIndex === index;
              const focused = focusedIndex === index;
              const active = focused || hovered;
              return {
                color: active
                  ? this.contrastPipe.transform(
                      focused ? this.baseColor : OPTION_HOVER_COLOR,
                    )
                  : 'inherit',
                background: focused
                  ? this.baseColor
                  : hovered
                    ? OPTION_HOVER_COLOR
                    : 'transparent',
              };
            }),
          ),
        ),
      ),
    );
  }

  private setOptionsScrollIntoView() {
    combineLatest([
      this.focusedOptionIndex$,
      this.optionRefs.changes.pipe(observeOn(asyncScheduler)),
    ])
      .pipe(
        filter(([index]) => index !== null && !!this.optionRefs.length),
        takeUntil(this.destroy$),
      )
      .subscribe(([index]) => {
        // prettier-ignore
        this.optionRefs
          .toArray()[index as number].nativeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center',
          });
      });
  }

  private stopEvent(event: Event) {
    event.stopPropagation();
    event.preventDefault();
  }

  private defaultTrackBy(index: number, option: T) {
    if (typeof option === 'string') {
      return option;
    }
    return index;
  }
}
